import React from 'react';
import GoogleMapsLoader from 'google-maps';
import $ from 'jquery';
import { gcApi } from 'config/axios';
import moment from 'moment';
import { t } from 'i18next';

import Personalizer from 'personalizer';

// Constants
import { EventActivityTypes } from 'constants/EventConstants';

// Utilities
import Conversion from 'utils/Conversion';
import DistanceUnits from 'utils/personalizer/DistanceUnits';
import TimeUtil from 'utils/date/TimeUtil';
import { getOffsetHHmm } from 'utils/DateUtils';
import PaceCalculator from 'utils/PaceCalculator';
import MapViewUtils from 'views/map/MapViewUtils';
import MeasurementSystems from 'utils/personalizer/MeasurementSystems';
import NumberFormats from 'utils/personalizer/NumberFormats';
import NumberParser from 'utils/parsers/NumberParser';
import NavigationUtil from 'utils/NavigationUtil';
import AnalyticsUtil from 'utils/AnalyticsUtil';
import formatNumber from 'utils/NumberFormatter';
import ActivityTypesUtil from 'utils/ActivityTypesUtil';
import ViewerSocialProfile from 'models/user/ViewerSocialProfile';
import ViewerUserPreferences from 'models/user/ViewerUserPreferences';
import LocaleMapper from 'utils/LocaleMapper';
import ActivityTypeMapper from 'utils/ActivityTypeMapper';
import { getLocationByCoordinates } from 'utils/HereApiUtil';
import { MinMaxRanges } from 'AdaptiveTrainingPlans/utils/ATPUtil';
import { DISTANCE_UNIT_MAPPER } from 'Calendar/utils/CalendarPageUtils';
import { formatDuration } from 'utils/TimeFormatter';
import { getShortDistanceUnitStr } from 'utils/UnitUtils';

import MapConstants from 'constants/map/map';

import { updateEvent, updateEventCustomization } from 'actions/calendarActions';

// General components
import Translate from 'components/Translate';

export const getDistanceInMeters = (distance, unit) => {
    let convertedDistance;

    switch (unit) {
        case 'mile':
            convertedDistance = Conversion.convertDistance(distance, DistanceUnits.mile, DistanceUnits.meter);
            break;
        case 'yard':
            convertedDistance = Conversion.convertDistance(distance, DistanceUnits.yard, DistanceUnits.meter);
            break;
        case 'meter':
            convertedDistance = Conversion.convertDistance(distance, DistanceUnits.meter, DistanceUnits.meter);
            break;
        case 'kilometer':
            convertedDistance = Conversion.convertDistance(distance, DistanceUnits.kilometer, DistanceUnits.meter);
            break;
        default:
            convertedDistance = distance;
    }

    return convertedDistance;
};

export const getSpeedWithoutDistance = (eventType, speedOrPace) => {
    let speed;
    let isMetric = Personalizer.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC;
    if (isRunningEventType(eventType)) {
        speed = PaceCalculator.convertPaceToSpeed(speedOrPace, isMetric ? DistanceUnits.kilometer : DistanceUnits.mile);
    } else if (isCyclingEventType(eventType)) {
        speed = PaceCalculator.convertToSpeed(speedOrPace, isMetric ? DistanceUnits.kilometer : DistanceUnits.mile);
    } else if (isSwimmingEventType(eventType)) {
        speed = Personalizer.convertSwimPaceToMetersPerSecond(
            TimeUtil.hoursMinutesAndSecondsToSeconds(hoursMinutesSeconds(speedOrPace)),
            100
        );
    }
    return speed;
};

/**
 * Makes sure that the time being returned is in hh:mm:ss format
 * @param {*} time
 */
export const hoursMinutesSeconds = (time) => {
    while (time.split(':').length < 3) {
        time = '00:' + time;
    }
    return time;
};

export const getSpeedWithSeconds = (eventType, seconds, distanceInMeters, getZeroIfOverHour = true) => {
    let speedValue;
    if (seconds > 0) {
        let mps = Conversion.convertToMetersPerSecond(DistanceUnits.meter, distanceInMeters, seconds);
        if (isCyclingEventType(eventType)) {
            speedValue = Personalizer.convertSpeed(mps, Personalizer.measurementSystem, eventType);
            speedValue = formatNumber(speedValue, Personalizer.numberSeparator, NumberFormats.PRECISION1);
        } else {
            speedValue = Personalizer.personalizeSpeed(mps, eventType, distanceInMeters, getZeroIfOverHour);
        }
    }
    return speedValue;
};

export const getSpeed = (eventType, time, distanceInMeters) => {
    return getSpeedWithSeconds(eventType, TimeUtil.hoursMinutesAndSecondsToSeconds(time), distanceInMeters);
};

export const getUnitKey = (unit) => {
    let key;

    switch (unit) {
        case DistanceUnits.yard:
            key = 'distance.yard';
            break;
        case DistanceUnits.meter:
            key = 'distance.meter';
            break;
        case DistanceUnits.kilometer:
            key = 'distance.kilometer';
            break;
        case DistanceUnits.mile:
            key = 'distance.mile';
            break;
        default:
            key = 'distance.mile';
            break;
    }
    return key;
};

export const ValidGoalEventTypes = [
    'cycling',
    'running',
    'gravel_cycling',
    'trail_running',
    'mountain_biking',
    'multi_sport_triathlon'
];

const RunningTypes = ['running', 'trail_running'];

const CyclingTypes = ['cycling', 'gravel_cycling', 'mountain_biking'];

const SwimmingTypes = ['swimming'];

export const isRunningEventType = (type) => {
    return RunningTypes.includes(type);
};

export const isCyclingEventType = (type) => {
    return CyclingTypes.includes(type);
};

export const isSwimmingEventType = (type) => {
    return SwimmingTypes.includes(type);
};

export const filterByType = (type, activityTypes) => {
    let types = [...activityTypes];

    if (isRunningEventType(type)) {
        types = activityTypes.filter((activity) => RunningTypes.includes(activity.typeKey));
    } else if (isCyclingEventType(type)) {
        types = activityTypes.filter((activity) => CyclingTypes.includes(activity.typeKey));
    } else if (isSwimmingEventType(type)) {
        types = activityTypes.filter((activity) => SwimmingTypes.includes(activity.typeKey));
    } else {
        types = activityTypes.filter((activity) => activity.typeKey === type);
    }

    return types;
};

export const searchAddressByPosition = (lat, lon, callback, errorCallback = () => {}) => {
    gcApi
        .get(`/geocoding-service/geocoding/reverse/all?lat=${lat}&lon=${lon}`)
        .then((response) => {
            let address = formatAddress(response.data);
            callback({
                address: address,
                location: [lat, lon]
            });
        })
        .catch((err) => {
            errorCallback();
        });
};

export const searchAddressByPositionUsingHere = (lat, lon, callback, errorCallback = () => {}) => {
    const locale = LocaleMapper.getFullLocale(ViewerUserPreferences.toJSON().preferredLocale);
    getLocationByCoordinates([lat, lon], { userLocale: locale }, (results) => {
        if (results && results?.length > 0) {
            let city = results[0].unFormattedAddress?.city;
            let state = results[0].unFormattedAddress?.stateCode;
            let country = results[0].unFormattedAddress?.countryName;
            /** filter out empty city or state so it won't show as undefined */
            let address = [city, state, country].filter((item) => !!item).join(', ');
            callback({
                address: address,
                location: results[0].location
            });
        } else {
            callback({
                address: '',
                location: [lat, lon]
            });
        }
    });
};

const formatAddress = (data) => {
    const city = data.filter((d) => {
        return d.adminType === 'City';
    });
    const state = data.filter((d) => {
        return d.adminType === 'State';
    });
    const county = data.filter((d) => {
        return d.adminType === 'County';
    });

    if (city.length > 0 && state.length > 0) {
        return `${city[0].localName}, ${state[0].localName}, ${city[0].countryCode}`;
    } else if (county.length > 0 && state.length > 0) {
        return `${county[0].localName}, ${state[0].localName}, ${county[0].countryCode}`;
    } else if (city.length > 0) {
        return `${city[0].localName}, ${city[0].countryCode}`;
    } else {
        return '';
    }
};

export const getPositionForAddress = (address, callback, errorCallback = () => {}) => {
    if (!MapViewUtils.userInChina()) {
        GoogleLoader(function(_google) {
            let geocoder = new _google.maps.Geocoder();

            geocoder.geocode({ address: address }, (results, status) => {
                if (status === _google.maps.GeocoderStatus.OK && results.length > 0) {
                    callback({
                        address: address,
                        formattedAddress: results[0].formatted_address,
                        location: [results[0].geometry.location.lat(), results[0].geometry.location.lng()]
                    });
                } else {
                    errorCallback({
                        message: 'Error finding address',
                        status: status
                    });
                }
            });
        });
    } else {
        let url = ['https://api.map.baidu.com/geocoder/v2/?'];
        url.push('ret_coordtype=bd09ll');
        url.push('&address=' + encodeURIComponent(address));
        url.push('&ak=' + MapConstants.baiduKey);
        url.push('&output=json');
        url.push('&callback=?');

        $.getJSON(url.join(''), (response) => {
            if (response.result) {
                var point = response.result.location;
                callback({
                    address: address,
                    formattedAddress: address,
                    location: [point.lat, point.lng]
                });
            } else {
                errorCallback({
                    message: 'Error finding address',
                    status: response.status
                });
            }
        }).fail((jqXHR, response, status) => {
            errorCallback({
                message: 'Error finding address',
                status: status
            });
        });
    }
};

const GoogleLoader = (loadedFunc) => {
    GoogleMapsLoader.VERSION = '3.18';
    GoogleMapsLoader.KEY = 'AIzaSyBuqMiuCNohSYg09UnOzu9Poy05jND5m3k';
    GoogleMapsLoader.CHANNEL = 'connect';
    GoogleMapsLoader.load(loadedFunc);
};

export const MaxDistance = {
    [DistanceUnits.mile]: 1000,
    [DistanceUnits.kilometer]: 1609,
    [DistanceUnits.meter]: 1609344,
    [DistanceUnits.yard]: 1760000
};

export const CyclingSpeedLimits = {
    MPH: {
        min: 0.1,
        max: 100
    },
    KPH: {
        min: 0.1,
        max: 132
    }
};

export const getUnitByMeasurementSystem = (unit) => {
    let personalizedUnit = unit;
    if (Personalizer.measurementSystem !== MeasurementSystems.METRIC) {
        if (unit === DistanceUnits.kilometer) {
            personalizedUnit = DistanceUnits.mile;
        } else if (unit === DistanceUnits.meter) {
            personalizedUnit = DistanceUnits.yard;
        }
    } else {
        if (unit === DistanceUnits.mile) {
            personalizedUnit = DistanceUnits.kilometer;
        } else if (unit === DistanceUnits.yard) {
            personalizedUnit = DistanceUnits.meter;
        }
    }
    return personalizedUnit;
};

export const isValidSpeed = (eventType, speedValue) => {
    const isMetric = Personalizer.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC;
    let valid = true;
    if (isCyclingEventType(eventType)) {
        let numberParser = new NumberParser({
            numberSeparator: Personalizer.getNumberSeparator()
        });
        try {
            const rawSpeed = numberParser.parse('' + speedValue);
            const maxSpeed = isMetric ? CyclingSpeedLimits.KPH.max : CyclingSpeedLimits.MPH.max;
            const minSpeed = isMetric ? CyclingSpeedLimits.KPH.min : CyclingSpeedLimits.MPH.min;
            if (rawSpeed <= maxSpeed && rawSpeed >= minSpeed) {
                valid = true;
            } else {
                valid = false;
            }
        } catch (err) {
            valid = false;
        }
    } else {
        if (speedValue && TimeUtil.hoursMinutesAndSecondsToSeconds(hoursMinutesSeconds(speedValue)) === 0) {
            valid = false;
        } else {
            valid = true;
        }
    }
    return valid;
};

export const GoalCompletionType = {
    Finish: 'Finish',
    Complete: 'Complete',
    LegTimeGoals: 'LegTimeGoals'
};

export const ValidEventTypes = [
    'running',
    'trail_running',
    'cycling',
    'gravel_cycling',
    'mountain_biking',
    'swimming',
    'multi_sport',
    'hiking',
    'walking',
    'fitness_equipment',
    'motorcycling_v2',
    'winter_sports',
    ActivityTypeMapper.PoolApnea,
    'other'
];

export const getFilterEventTypeOptions = (showTriathlonEvents) => {
    const options = [
        <Translate key={'all'} tag="option" ns="events-based-goals" attr={{ value: 'all' }} content={`all`} />,
        <Translate
            key={'running'}
            tag="option"
            ns="events-based-goals"
            attr={{ value: 'running' }}
            content={`all_running`}
        />,
        <Translate
            key={'trail_running'}
            tag="option"
            ns="activity_types"
            attr={{ value: 'trail_running' }}
            content={`activity_type_trail_running`}
        />,
        <Translate
            key={'all_cycling'}
            tag="option"
            ns="events-based-goals"
            attr={{ value: 'cycling' }}
            content={`all_cycling`}
        />,
        <Translate
            key={'gravel_cycling'}
            tag="option"
            ns="activity_types"
            attr={{ value: 'gravel_cycling' }}
            content={`activity_type_gravel_cycling`}
        />,
        <Translate
            key={'mountain_biking'}
            tag="option"
            ns="activity_types"
            attr={{ value: 'mountain_biking' }}
            content={`activity_type_mountain_biking`}
        />,
        <Translate
            key={'swimming'}
            tag="option"
            ns="activity_types"
            attr={{ value: 'swimming' }}
            content={`activity_type_swimming`}
        />,
        <Translate
            key={'multi_sport'}
            tag="option"
            ns="activity_types"
            attr={{ value: 'multi_sport' }}
            content={`activity_type_multi_sport`}
        />,
        <Translate
            key={'hiking'}
            tag="option"
            ns="activity_types"
            attr={{ value: 'hiking' }}
            content={`activity_type_hiking`}
        />,
        <Translate
            key={'walking'}
            tag="option"
            ns="activity_types"
            attr={{ value: 'walking' }}
            content={`activity_type_walking`}
        />,
        <Translate
            key={'fitness_equipment'}
            tag="option"
            ns="activity_types"
            attr={{ value: 'fitness_equipment' }}
            content={`activity_type_fitness_equipment`}
        />,
        <Translate
            key={'motorcycling_v2'}
            tag="option"
            ns="activity_types"
            attr={{ value: 'motorcycling_v2' }}
            content={`activity_type_motorcycling_v2`}
        />,
        <Translate
            key={'winter_sports'}
            tag="option"
            ns="activity_types"
            attr={{ value: 'winter_sports' }}
            content={`activity_type_winter_sports`}
        />,
        <Translate
            key={'other'}
            tag="option"
            ns="activity_types"
            attr={{ value: 'other' }}
            content={`activity_type_other`}
        />
    ];

    // insert Triathlon above Multisport
    if (showTriathlonEvents) {
        const multisportIndex = options.map((option) => option.key).indexOf('multi_sport');
        options.splice(
            multisportIndex,
            0,
            <Translate
                key={'multi_sport_triathlon'}
                tag="option"
                ns="activity_types"
                attr={{ value: 'multi_sport_triathlon' }}
                content={`activity_type_multi_sport_triathlon`}
            />
        );
    }

    return options;
};

export const getValidEventTypes = (activityTypes) => {
    const parentActivityTypes = ValidEventTypes.map((type) => {
        let activityType = activityTypes.find((activity) => type === activity.typeKey);
        if (activityType) {
            return {
                typeKey: type
            };
        }
    }).filter((type) => {
        return !!type;
    });
    return parentActivityTypes;
};

export const getParentEventType = (activityTypes, eventType) => {
    // CA-136656: Slow migration from motorcycling to motorcycling_v2
    if (eventType === 'motorcycling') {
        eventType = 'motorcycling_v2';
    }
    const eventParentType = eventType ? new ActivityTypesUtil(activityTypes).getParentType(eventType)?.typeKey : '';
    return eventParentType;
};

export const FilterTypes = {
    dateRange: 'dateRange',
    timeRange: 'timeRange',
    distanceRange: 'distanceRange',
    eventType: 'eventType',
    inPerson: 'inPerson',
    virtual: 'virtual',
    OFFICIAL: 'OFFICIAL',
    VERIFIED: 'VERIFIED',
    NONE: 'NONE',
    activeEvents: 'activeEvents',
    garminEvents: 'garminEvents',
    filterRadius: 'filterRadius',
    duration: 'duration'
};

export const formatPaceValue = (pace) => {
    let formattedPace = pace;
    if (!!pace && pace.split(':').length > 0) {
        let pieces = pace.split(':');
        let minutes = pieces[0];
        let seconds = pieces[1];
        formattedPace = minutes.padStart(2, '0') + ':' + seconds.padEnd(2, '0');
    }
    return formattedPace;
};

export const formatAddressFromAdministrativeArea = (administrativeArea) => {
    let address = '---';
    if (administrativeArea) {
        if (administrativeArea.cityNative) {
            address = administrativeArea.cityNative;
            if (administrativeArea.stateNative) {
                address += ', ' + administrativeArea.stateNative;
            }
            if (administrativeArea.countryCode) {
                address += ', ' + administrativeArea.countryCode;
            }
        } else if (administrativeArea.cityEn) {
            address = administrativeArea.cityEn;
            if (administrativeArea.stateEn) {
                address += ', ' + administrativeArea.stateEn;
            }
            if (administrativeArea.countryCode) {
                address += ', ' + administrativeArea.countryCode;
            }
        }
    }
    return address;
};

export const getCustomPaceProParams = (event) => {
    let goalTimeInSecs = 0;
    let pace = '';
    let distanceInMeters, formatSeconds;
    let params = {
        time: null,
        pace: null,
        distance: null
    };

    if (event.completionTarget && event.eventCustomization && event.eventCustomization.customGoal) {
        if (event.completionTarget.unitType === 'distance') {
            if (event.eventCustomization.customGoal.unitType === 'time') {
                goalTimeInSecs = event.eventCustomization.customGoal.value;

                // adjust goal time to the run leg goal time if it's a triathlon event
                if (event.eventType === EventActivityTypes.triathlon) {
                    const runLegGoal = getTriathlonLegGoal(event, EventActivityTypes.running);
                    goalTimeInSecs = runLegGoal?.value || goalTimeInSecs;
                }
                distanceInMeters = getDistanceInMeters(event.completionTarget.value, event.completionTarget.unit);
                formatSeconds = TimeUtil.formatSeconds(goalTimeInSecs);
                pace = getSpeed(event.eventType, formatSeconds, distanceInMeters);
            }
        } else {
            if (event.eventCustomization.customGoal.unitType === 'distance') {
                goalTimeInSecs = event.completionTarget.value;
                distanceInMeters = getDistanceInMeters(
                    event.eventCustomization.customGoal.value,
                    event.eventCustomization.customGoal.unit
                );
                formatSeconds = TimeUtil.formatSeconds(goalTimeInSecs);
                pace = getSpeed(event.eventType, formatSeconds, distanceInMeters);
            }
        }

        params.time = goalTimeInSecs;
        params.pace = pace;
        params.distance = distanceInMeters;
    }

    return params;
};

/**
 * Description: Get the completion target leg of the given event for the given leg type (running/cycling/swimming)
 *
 * @param {object} event
 * @param {string} legType
 */
export const getTriathlonLegTarget = (event, legType) => {
    return event?.completionTargetLegs?.find((legTarget) => {
        return [
            EventActivityTypes.openWaterSwimming,
            EventActivityTypes.poolSwimming,
            EventActivityTypes.swimming
        ].includes(legType.toLowerCase())
            ? [
                  EventActivityTypes.openWaterSwimming,
                  EventActivityTypes.poolSwimming,
                  EventActivityTypes.swimming
              ].includes(legTarget?.eventLegActivityType.toLowerCase())
            : legTarget.eventLegActivityType.toLowerCase() === legType.toLowerCase();
    });
};

/**
 * Description: Get the leg goal object for an event for the given legType (running/cycling/swimming)
 *
 * @param {object} event
 * @param {string} legType
 */
export const getTriathlonLegGoal = (event, legType) => {
    const targetLegIndex = getTriathlonLegTarget(event, legType)?.legIndex;
    const customLegGoal = event?.eventCustomization?.customLegGoals?.find((legGoal) => {
        return legGoal?.legIndex === targetLegIndex;
    });

    return customLegGoal;
};

export const eventDateToMonthDayYearForCalendar = (eventDate) => {
    let dateArray = eventDate.split('-');
    let year = dateArray[0];
    let month = parseInt(dateArray[1], 10) - 1; // expecting month to be 0 based
    let day = dateArray[2];
    return {
        month: ('' + month).padStart(2, '0'),
        year: year,
        day: day
    };
};

export const getTabForEvents = (tab) => {
    switch (tab) {
        case 'event':
            return t('daily_summary:card_type_event');
        case 'calendar':
            return t('widgets:title_calendar_widget');
        case 'course':
            return t('activity:course_label');
        case 'activity':
            return t('common:activity');
        case 'pace-pro':
            return t('widgets:label_pace_pro');
        case 'home':
            return t('common:default_landing_homepage');
        default:
            return t('daily_summary:card_type_event');
    }
};

export const getEventDetailsUrl = (eventId, shareableEventUuid, tab, eventDate, isEventNewPageEnabled) => {
    if (isEventNewPageEnabled) {
        const shareableUuidOrId = shareableEventUuid ? `?shareableEventUuid=${shareableEventUuid}` : '';
        const page = tab ? `/event/${tab}/` : '/event/0/';
        const url = `${page}${eventId}${shareableUuidOrId}`;
        return url;
    } else {
        let monthYear = eventDateToMonthDayYearForCalendar(eventDate);
        let url = `/calendar/${monthYear.year}/${
            monthYear.month
        }?shareable=${!!shareableEventUuid}&eventId=${eventId}&shareableEventUuid=${
            shareableEventUuid ? shareableEventUuid : ''
        }&eventEdit=true&r=${Date.now()}`; // Need to change the URL for navigate to work if calling the same url
        return url;
    }
};

export const navigateToEventDetails = (
    eventId,
    shareableEventUuid,
    eventDate,
    refresh = false,
    clickEvent = null,
    tab,
    isEventNewPageEnabled
) => {
    let url = getEventDetailsUrl(eventId, shareableEventUuid, tab, eventDate, isEventNewPageEnabled);
    if (refresh) {
        window.location.href = NavigationUtil.url(url);
    } else {
        NavigationUtil.navigate(url, clickEvent, { trigger: true });
    }
};

export const navigateToEventTrainingPlan = (eventId) => {
    let url = `/event-training-plan/${eventId}`;
    NavigationUtil.navigate(url, null, { trigger: true });
};

export const getSpeedFromEvent = (event) => {
    let distanceInMeters = 0;
    let mps = 0;

    if (event.completionTarget) {
        if (event.eventCustomization?.customGoal?.unitType === 'time') {
            distanceInMeters = Conversion.convertDistance(
                event.completionTarget.value,
                event.completionTarget.unit,
                DistanceUnits.meter
            );
            mps = Conversion.convertToMetersPerSecond(
                DistanceUnits.meter,
                distanceInMeters,
                event.eventCustomization.customGoal.value
            );
        } else if (event.eventCustomization?.customGoal?.unitType === 'distance') {
            distanceInMeters = Conversion.convertDistance(
                event.eventCustomization.customGoal.value,
                event.eventCustomization.customGoal.unit,
                DistanceUnits.meter
            );
            mps = Conversion.convertToMetersPerSecond(
                DistanceUnits.meter,
                distanceInMeters,
                event.completionTarget.value
            );
        }
    }

    let speedValue = Personalizer.personalizeSpeed(mps, event.eventType || 'Running', distanceInMeters);
    return speedValue;
};

export const linkActivity = (event) => {
    let linkInfo = {
        calendarEventId: event.shareableEventUuid ? null : event.id,
        calendarEventUuid: event.shareableEventUuid,
        completionTargetValue: event.completionTarget ? event.completionTarget.value : null,
        completionTargetUnitType: event.completionTarget ? event.completionTarget.unitType : null
    };

    if (
        linkInfo.completionTargetValue &&
        linkInfo.completionTargetUnitType &&
        linkInfo.completionTargetUnitType === 'distance'
    ) {
        // Convert to meters
        linkInfo.completionTargetValue = Conversion.convertDistance(
            linkInfo.completionTargetValue,
            event.completionTarget.unit,
            'meter'
        );
    }

    if (event.eventCustomization && event.eventCustomization.customGoal) {
        if (event.eventCustomization.customGoal.unitType === 'time') {
            linkInfo.customGoalUnitType = 'time';
            linkInfo.customGoalValue = event.eventCustomization.customGoal.value;
        } else if (event.eventCustomization.customGoal.unitType === 'distance') {
            linkInfo.customGoalUnitType = 'distance';
            // Convert to meters
            linkInfo.customGoalValue = Conversion.convertDistance(
                event.eventCustomization.customGoal.value,
                event.eventCustomization.customGoal.unit,
                'meter'
            );
        }
    }
    return linkInfo;
};

const LocalStoreageKey = 'LinkedEventSaved';

export const storeSavedEventMessage = (messageKey, eventName) => {
    localStorage.setItem(
        LocalStoreageKey,
        JSON.stringify({
            messageKey: messageKey,
            eventName: eventName
        })
    );
    // Make sure that this gets cleared out
    setTimeout(() => {
        clearSavedEventMessage();
    }, 15000);
};

export const getSavedEventMessage = () => {
    let info = localStorage.getItem(LocalStoreageKey);
    if (info) {
        info = JSON.parse(info);
    }
    return info;
};

export const clearSavedEventMessage = () => {
    localStorage.removeItem(LocalStoreageKey);
};

export const pushAnalyticsEvent = (action) => {
    AnalyticsUtil.push({
        event: 'track_event',
        category: 'Training Initiative Event',
        action: action
    });
};

export const isEventFutureDate = (event) => {
    let now = moment();
    let time = null;
    if (event.eventTimeLocal) {
        time = event.eventTimeLocal.startTimeHhMm.split(':');
    } else {
        time = [24, 59]; // EOD
    }
    let eventDate = moment(event.date).set({ hour: time[0], minute: time[1], seconds: now.second() });
    let future = now.isBefore(eventDate);
    return future;
};

export const fetchEventsForDailySummary = (summaryDate) => {
    const summary = toDateFromString(summaryDate);
    const current = new Date();
    current.setHours(0, 0, 0, 0);
    // Should only make call if summaryDate is today. All other events have passed.
    return summary >= current;
};

export const toDateFromString = (dateString) => {
    // Expected format = YYYY-MM-DD
    return new Date(`${dateString}T00:00:00`);
};

export const isGoalMetOrExceeded = (activity) => {
    let met = false;

    if (activity && activity.metadataDTO && activity.metadataDTO.calendarEventInfo) {
        if (activity.metadataDTO.calendarEventInfo.customGoalUnitType === 'time') {
            met = activity.metadataDTO.calendarEventInfo.customGoalValue >= activity.summaryDTO.duration;
        } else if (activity.metadataDTO.calendarEventInfo.customGoalUnitType === 'distance') {
            met = activity.metadataDTO.calendarEventInfo.customGoalValue >= activity.summaryDTO.distance;
        }
    }

    return met;
};

export const isDistanceGoal = (activity) => {
    return activity.metadataDTO?.calendarEventInfo?.customGoalUnitType === 'distance';
};

export const getRandomEventGoalMetBannerTitle = () => {
    let random = Math.floor(Math.random() * 3);

    switch (random) {
        case 0:
            return <Translate ns="events-based-goals" content="event_goal_title_nice_job" />;
        case 1:
            return <Translate ns="events-based-goals" content="event_goal_title_great_work" />;
        case 2:
            return <Translate ns="events-based-goals" content="event_goal_title_well_done" />;
    }
};

export const setUserSeenEventGoalMetBanner = (activityId) => {
    localStorage.setItem(`EventGoalMetBanner-${activityId}`, 'true');
};

export const hasUserSeenBanner = (activityId) => {
    let item = localStorage.getItem(`EventGoalMetBanner-${activityId}`);
    return !!item && item === 'true';
};

export const formatDate = (date) => {
    const [dateStr] = new Date(date).toISOString().split('T');
    return dateStr;
};

export const isFromGroupsPage = () => {
    return window.location.href.indexOf('/group/') > -1;
};

export const isFutureDate = (event) => {
    let now = moment();
    let time = null;
    if (event.eventTimeLocal) {
        time = event.eventTimeLocal.startTimeHhMm.split(':');
    } else {
        time = [now.hour(), now.minute()];
    }
    let eventDate = moment(event.date).set({ hour: time[0], minute: time[1], seconds: now.second() });
    let future = now.isBefore(eventDate);
    return future;
};

export const getTimeZone = (lat, lon, success = () => {}, error = () => {}) => {
    gcApi
        .get(`geocoding-service/geocoding/timezone?lat=${lat}&lon=${lon}`)
        .then((response) => {
            success(response);
        })
        .catch(() => {
            error();
        });
};

export const CourseActionValues = {
    Select: 'Select',
    Find: 'Find',
    Import: 'Import',
    Create: 'Create'
};

export const validateUrl = async (url) => {
    let retObj = {};

    try {
        let response = await gcApi.post('/url-validation/validation/url', url, {
            headers: {
                'content-type': 'text/plain'
            }
        });
        if (response.data) {
            retObj = {
                valid: true,
                score: response.data.reputationScore,
                url: response.data.normalizedUrl
            };
        }
    } catch (err) {
        retObj = {
            valid: false,
            score: null,
            url: null
        };
    }

    return retObj;
};

export const TrainingPriorityOptions = {
    Primary: 'Primary',
    Support: 'Support',
    None: 'None'
};

export const updateTrainingPriority = (event, priority, onSuccess, onError) => {
    let eventCustomization = {
        ...event.eventCustomization,
        isPrimaryEvent: false,
        isTrainingEvent: false
    };

    if (priority === TrainingPriorityOptions.Primary) {
        eventCustomization.isPrimaryEvent = true;
    } else if (priority === TrainingPriorityOptions.Support) {
        eventCustomization.isPrimaryEvent = false;
        eventCustomization.isTrainingEvent = true;
    } else {
        eventCustomization.isPrimaryEvent = false;
        eventCustomization.isTrainingEvent = false;
    }

    if (event.shareableEventUuid) {
        updateEventCustomization(event.shareableEventUuid, eventCustomization)
            .then(() => {
                onSuccess();
            })
            .catch((err) => {
                onError(err);
            });
    } else {
        event.eventCustomization = eventCustomization;
        updateEvent(NavigationUtil.url, event.id, event)
            .then(() => {
                onSuccess();
            })
            .catch((err) => {
                onError(err);
            });
    }
};

export const earnBadgeWhenSharing = () => {
    gcApi.post(`/badge-service/badge/earn`, { badgeId: 1549 });
};

export const isVerifiedStatusEventDetails = (event) => {
    return event?.verifiedStatus === 'VERIFIED' && !isOfficialStatusEventDetails(event);
};

export const isOfficialStatusEventDetails = (event) => {
    return event?.statuses && event?.statuses.includes('OFFICIAL');
};

export const isVerifiedStatusSearch = (event) => {
    return event.verifiedStatus === 'VERIFIED' && !isOfficialStatusSearch(event);
};

export const isOfficialStatusSearch = (event) => {
    return event.isOfficial;
};

export const hasRole = (role) => {
    return ViewerSocialProfile.toJSON().userRoles.includes(role);
};

export const getProviderName = (providers, provider) => {
    let found = providers?.find((p) => {
        return p.eventsProviderKey === provider;
    });
    return found ? found.eventsProviderName : '';
};

export const getSpeedFromCoachPlan = (event) => {
    let distanceInMeters = 0;
    let mps = 0;

    if (event.completionTarget && event.eventCustomization.customGoal.unitType === 'time') {
        distanceInMeters = Conversion.convertDistance(
            event.completionTarget.value,
            event.completionTarget.unit,
            DistanceUnits.meter
        );

        Object.keys(MinMaxRanges).forEach((key) => {
            let range = MinMaxRanges[key];
            if (distanceInMeters >= range.startDistance && distanceInMeters <= range.endDistance) {
                switch (key) {
                    case '5K':
                        distanceInMeters = 5000;
                        break;
                    case '10K':
                        distanceInMeters = 10000;
                        break;
                    case 'HALFMARATHON':
                        distanceInMeters = 21097.5;
                        break;
                }
            }
        });

        mps = Conversion.convertToMetersPerSecond(
            DistanceUnits.meter,
            distanceInMeters,
            event.eventCustomization.customGoal.value
        );
    }

    let speedValue = Personalizer.personalizeSpeed(mps, event.eventType || 'Running', distanceInMeters);
    return speedValue;
};

export const personalizeEventDistance = (event, t) => {
    if (
        !Personalizer.isSwimmingActivity(event.eventType) &&
        !Personalizer.isDisplayInMeter(event.eventType) &&
        event.completionTarget?.unitType === 'distance'
    ) {
        // convert the distance to miles or kilometers
        const isMetric = Personalizer.isMetricMeasurementSystem();
        let convertedDistance = Conversion.convertDistance(
            event.completionTarget.value,
            event.completionTarget.unit,
            isMetric ? DistanceUnits.kilometer : DistanceUnits.mile
        );

        return {
            distance: Personalizer.formatDistance(convertedDistance, event.eventType),
            unit: t ? (
                t(`units:${DISTANCE_UNIT_MAPPER[isMetric ? DistanceUnits.kilometer : DistanceUnits.mile]}`)
            ) : (
                <Translate
                    ns="units"
                    content={DISTANCE_UNIT_MAPPER[isMetric ? DistanceUnits.kilometer : DistanceUnits.mile]}
                />
            )
        };
    } else if (event.completionTarget?.value) {
        return {
            distance: Personalizer.formatDistance(event.completionTarget.value, event.eventType),
            unit: t ? (
                t(`units:${DISTANCE_UNIT_MAPPER[event.completionTarget.unit]}`)
            ) : (
                <Translate ns="units" content={DISTANCE_UNIT_MAPPER[event.completionTarget.unit]} />
            )
        };
    } else {
        return null;
    }
};

export const convertedEventDistance = (event) => {
    if (
        !Personalizer.isSwimmingActivity(event.eventType) &&
        !Personalizer.isDisplayInMeter(event.eventType) &&
        event.completionTarget?.unitType === 'distance'
    ) {
        // convert the distance to miles or kilometers
        const isMetric = Personalizer.isMetricMeasurementSystem();
        let convertedDistance = Conversion.convertDistance(
            event.completionTarget.value,
            event.completionTarget.unit,
            isMetric ? DistanceUnits.kilometer : DistanceUnits.mile
        );

        return {
            distance: convertedDistance,
            unit: isMetric ? DistanceUnits.kilometer : DistanceUnits.mile
        };
    } else if (event.completionTarget?.value) {
        return {
            distance: event.completionTarget.value,
            unit: event.completionTarget.unit
        };
    } else {
        return null;
    }
};

/**
 * Description: Return the number of colons in the input string
 *
 * @param {string} input
 * @returns {number}
 */
const getColonCounts = (input) => {
    let count = 0;
    let colonArray = input.match(/:/g);
    if (colonArray) {
        count = colonArray.length;
    }
    return count;
};

/**
 * Description: Return a time formatted string based on how the user changed the input
 *  Example: 123 -> 12:3
 *
 * @param {string} oldVal
 * @param {string} newVal
 * @returns {string}
 */
export const onTimeInputChange = (oldVal, newVal) => {
    let newValue = newVal;
    let updateValue = true;

    if (newValue.length > oldVal.length) {
        if (newValue.substr(-1) === ':') {
            // input colon case
            let insertZero = false;
            let colonCounts = getColonCounts(newValue);
            if (colonCounts > 2) {
                updateValue = false;
            } else if (newValue.length === 1 || newValue.substr(-2, 2) === '::') {
                insertZero = true;
            }

            // e.g., ":" -> "0:"
            // e.g., "15::" -> "15:0:"
            if (updateValue && insertZero) {
                newValue = newValue.substr(0, newValue.length - 1) + '0:';
            }
        } else {
            // other input cases
            const LIMIT = 60;
            let insertColon = false;
            let pos = newValue.lastIndexOf(':');

            let valueToCheck = newValue;
            if (pos !== -1) {
                valueToCheck = newValue.substr(pos + 1);
            }

            if (Number(valueToCheck) >= LIMIT || valueToCheck.length > 2) {
                insertColon = true;
            }

            if (insertColon) {
                let colonCounts = getColonCounts(newValue);
                if (colonCounts < 2) {
                    // e.g., "66"   -> "6:6"
                    // e.g., "123"  -> "12:3"
                    // e.g., "6:66" -> "6:6:6"
                    newValue = newValue.substr(0, newValue.length - 1) + ':' + newValue.substr(-1);
                } else {
                    // we should add no more colon
                    updateValue = false;
                }
            }
        }
    } else {
        // Backspace case
        if (newValue.substr(-1) === ':') {
            // remove the final ':'
            // e.g., "25:1" -> hit backspace -> "25"
            newValue = newValue.substr(0, newValue.length - 1);
        }
    }

    return newValue;
};

/**
 * Description: Return a pace formatted string based on how the user changed the input
 *  Example: 123 -> 12:3
 *
 * @param {string} oldVal
 * @param {string} newVal
 * @returns {string}
 */
export const onPaceInputChange = (oldVal, newVal) => {
    let newValue = newVal;

    if (newValue.length > oldVal.length) {
        if (newValue.substr(-1) !== ':') {
            // other input cases
            const LIMIT = 60;
            let insertColon = false;
            let pos = newValue.lastIndexOf(':');

            let valueToCheck = newValue;
            if (pos !== -1) {
                valueToCheck = newValue.substr(pos + 1);
            }

            if (Number(valueToCheck) >= LIMIT || valueToCheck.length > 2) {
                insertColon = true;
            }

            if (insertColon) {
                let colonCounts = getColonCounts(newValue);
                if (colonCounts < 1) {
                    // e.g., "66"   -> "6:6"
                    // e.g., "123"  -> "12:3"
                    newValue = newValue.substr(0, newValue.length - 1) + ':' + newValue.substr(-1);
                }
            }
        }
    } else {
        // Backspace case
        if (newValue.substr(-1) === ':') {
            // remove the final ':'
            // e.g., "25:1" -> hit backspace -> "25"
            newValue = newValue.substr(0, newValue.length - 1);
        }
    }

    return newValue;
};

/**
 * Description: Use leg distance and leg pace/speed to calculate a string representing time to complete a leg of the event in format hh:mm:ss
 * @param {string} legType
 * @param {string} legDistance
 * @param {string} legUnit
 * @param {string} legPaceSpeed
 * @returns {string}
 */
export const calculateTimeFromPaceOrSpeed = (legType, legDistance, legUnit, legPaceSpeed) => {
    let value = '';

    if (legPaceSpeed) {
        switch (legType) {
            case EventActivityTypes.swimming:
                const formattedPace = TimeUtil.timeFormatter(legPaceSpeed);
                // swim leg is always in meters, no statute conversion necessary
                // ({pace}seconds / {100}meters) * {legDistance}meters
                const calculatedSwimTime =
                    (TimeUtil.hoursMinutesAndSecondsToSeconds(formattedPace) / 100) *
                    Conversion.convertDistance(legDistance, legUnit, 'meter');
                value = formatDuration(calculatedSwimTime, 'seconds', 'hh:mm:ss', false);
                break;
            case EventActivityTypes.cycling:
                const convertedBikeSpeed = getSpeedWithoutDistance(EventActivityTypes.cycling, legPaceSpeed);
                const calculatedBikeTime = PaceCalculator.calculateTimeWithActual(
                    EventActivityTypes.cycling,
                    Conversion.convertDistance(legDistance, legUnit, 'meter'),
                    convertedBikeSpeed
                );

                value = formatDuration(
                    TimeUtil.hoursMinutesAndSecondsToSeconds(
                        TimeUtil.timeFormatter(calculatedBikeTime?.time || '0:00')
                    ),
                    'seconds',
                    'hh:mm:ss',
                    false
                );
                break;
            case EventActivityTypes.running:
                // const updatedPaceStr = onPaceInputChange(runPace, value);
                const convertedRunPace = getSpeedWithoutDistance(
                    EventActivityTypes.running,
                    formatDuration(
                        TimeUtil.hoursMinutesAndSecondsToSeconds(TimeUtil.timeFormatter(legPaceSpeed)),
                        'seconds',
                        'mm:ss',
                        false
                    )
                );
                const calculatedRunTime = PaceCalculator.calculateTimeWithActual(
                    EventActivityTypes.running,
                    Conversion.convertDistance(legDistance, legUnit, 'meter'),
                    convertedRunPace,
                    false
                );
                value = formatDuration(
                    TimeUtil.hoursMinutesAndSecondsToSeconds(TimeUtil.timeFormatter(calculatedRunTime?.time || '0:00')),
                    'seconds',
                    'hh:mm:ss',
                    false
                );
                break;
        }
    }

    return value;
};

/**
 * Description: Use leg distance and leg time to calculate a string representing the pace/speed to complete a leg of the event
 *
 * @param {string} legType
 * @param {string} legDistance
 * @param {string} legUnit
 * @param {string} legTime
 * @returns {string}
 */
export const calculatePaceOrSpeedFromTime = (legType, legDistance, legUnit, legTime) => {
    let value = '';

    if (legTime) {
        switch (legType) {
            case EventActivityTypes.swimming:
                const distanceInMeters = Conversion.convertDistance(legDistance, legUnit, 'meter');
                const calculatedPace =
                    (TimeUtil.hoursMinutesAndSecondsToSeconds(TimeUtil.timeFormatter(legTime)) / distanceInMeters) *
                    100;
                value = formatDuration(calculatedPace, 'seconds', 'm:ss', false);
                break;
            case EventActivityTypes.cycling:
                const bikeSeconds = TimeUtil.hoursMinutesAndSecondsToSeconds(TimeUtil.timeFormatter(legTime));
                const bikeSpeedValue = getSpeedWithSeconds(
                    EventActivityTypes.cycling,
                    bikeSeconds,
                    Conversion.convertDistance(legDistance, legUnit, 'meter')
                );
                value = bikeSpeedValue;
                break;
            case EventActivityTypes.running:
                const runSeconds = TimeUtil.hoursMinutesAndSecondsToSeconds(TimeUtil.timeFormatter(legTime));
                const runPaceValue = getSpeedWithSeconds(
                    EventActivityTypes.running,
                    runSeconds,
                    Conversion.convertDistance(legDistance, legUnit, 'meter'),
                    false
                );
                value = runPaceValue;
                break;
        }
    }

    return value;
};

/**
 * Is the event date, excluding time, in the future.
 * Used for virutal events.
 * @param {*} event
 * @returns
 */
export const isFutureDateNoTime = (event) => {
    let now = moment();
    let eventDate = moment(event.date).set({ hour: 0, minute: 0, seconds: 0 });
    let future = now.isBefore(eventDate);
    return future;
};

export const getTriathlonLegInfo = (event) => {
    const hasTriathlonLegGoals =
        event?.eventType === EventActivityTypes.triathlon && event?.eventCustomization?.customLegGoals?.length > 0;

    // get triathlon leg targets for swim/bike/run
    const triathlonSwimLegTarget = getTriathlonLegTarget(event, EventActivityTypes.poolSwimming);
    const triathlonBikeLegTarget = getTriathlonLegTarget(event, EventActivityTypes.cycling);
    const triathlonRunLegTarget = getTriathlonLegTarget(event, EventActivityTypes.running);

    // get the triathlon swim/bike/run/transition goal
    const triathlonSwimLegGoal = getTriathlonLegGoal(event, EventActivityTypes.poolSwimming);
    const triathlonBikeLegGoal = getTriathlonLegGoal(event, EventActivityTypes.cycling);
    const triathlonRunLegGoal = getTriathlonLegGoal(event, EventActivityTypes.running);

    const triathlonLegInfo = {
        swimTime: '',
        swimPace: '',
        transitionOneTime: '',
        bikeTime: '',
        bikeSpeed: '',
        transitionTwoTime: '',
        runTime: '',
        runPace: ''
    };

    if (event?.eventType === EventActivityTypes.triathlon && hasTriathlonLegGoals) {
        triathlonLegInfo.swimTime = formatDuration(
            triathlonSwimLegGoal.value,
            'seconds',
            triathlonSwimLegGoal.value ? 'h:mm:ss' : 'm:ss'
        );
        triathlonLegInfo.swimPace = `(${calculatePaceOrSpeedFromTime(
            EventActivityTypes.swimming,
            triathlonSwimLegTarget.value,
            triathlonSwimLegTarget.unit,
            triathlonLegInfo.swimTime
        )} /100${getShortDistanceUnitStr('meter')})`;

        triathlonLegInfo.transitionOneTime = formatDuration(
            triathlonSwimLegGoal.transitionOutDurationSeconds,
            'seconds',
            triathlonSwimLegGoal.transitionOutDurationSeconds >= 3600 ? 'h:mm:ss' : 'm:ss'
        );

        triathlonLegInfo.bikeTime = formatDuration(
            triathlonBikeLegGoal.value,
            'seconds',
            triathlonBikeLegGoal.value >= 3600 ? 'h:mm:ss' : 'm:ss'
        );
        triathlonLegInfo.bikeSpeed = `(${calculatePaceOrSpeedFromTime(
            EventActivityTypes.cycling,
            triathlonBikeLegTarget.value,
            triathlonBikeLegTarget.unit,
            triathlonLegInfo.bikeTime
        )} ${
            Personalizer.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC
                ? t('units:speed_unit_metric')
                : t('units:speed_unit_statute')
        })`;

        triathlonLegInfo.transitionTwoTime = formatDuration(
            triathlonBikeLegGoal.transitionOutDurationSeconds,
            'seconds',
            triathlonBikeLegGoal.transitionOutDurationSeconds >= 3600 ? 'h:mm:ss' : 'm:ss'
        );

        triathlonLegInfo.runTime = formatDuration(
            triathlonRunLegGoal.value,
            'seconds',
            triathlonRunLegGoal.value >= 3600 ? 'h:mm:ss' : 'm:ss'
        );
        triathlonLegInfo.runPace = `(${calculatePaceOrSpeedFromTime(
            EventActivityTypes.running,
            triathlonRunLegTarget.value,
            triathlonRunLegTarget.unit,
            triathlonLegInfo.runTime
        )} /${getShortDistanceUnitStr(
            Personalizer.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC ? 'kilometer' : 'mile'
        )})`;
    }
    return triathlonLegInfo;
};

/**
 * We need to make sure that the bike leg's value and unit is either mile or kilometer
 * @param {*} bikeLeg
 * @returns
 */
export const personalizeBikeLeg = (bikeLeg) => {
    if (bikeLeg) {
        if (Personalizer.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC) {
            bikeLeg.value = Conversion.convertDistance(bikeLeg.value, bikeLeg.unit, DistanceUnits.kilometer);
            bikeLeg.unit = DistanceUnits.kilometer;
        } else {
            bikeLeg.value = Conversion.convertDistance(bikeLeg.value, bikeLeg.unit, DistanceUnits.mile);
            bikeLeg.unit = DistanceUnits.mile;
        }
    }
    return bikeLeg;
};

/**
 * We need to make sure that the run leg's value and unit is either mile or kilometer
 * @param {*} runLeg
 * @returns
 */
export const personalizeRunLeg = (runLeg) => {
    if (runLeg) {
        if (Personalizer.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC) {
            runLeg.value = Conversion.convertDistance(runLeg.value, runLeg.unit, DistanceUnits.kilometer);
            runLeg.unit = DistanceUnits.kilometer;
        } else {
            runLeg.value = Conversion.convertDistance(runLeg.value, runLeg.unit, DistanceUnits.mile);
            runLeg.unit = DistanceUnits.mile;
        }
    }
    return runLeg;
};
